import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { AuthenService } from '@core/authen/authen.service';
import { BaseHttpService } from '@core/http/base-http.service';
import { SessionService } from '@core/session.service';
import { StorageEnum } from '@shared/enums/storage.enum';
import { CookieUtils } from '@shared/utils/cookie-utils';
import { TimeUtils } from '@shared/utils/time-utils';
import compare from 'just-compare';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, lastValueFrom, of } from 'rxjs';
import {
  filter,
  first,
  map,
  mergeMap,
  shareReplay,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

type UserConfig = Record<string, object>;
interface UserConfigRes {
  data: UserConfig;
}
interface CookieOptions {
  expires?: number | Date;
  path?: string;
}

// Browsers drop a cookie above ~4096 bytes (name + value); stay clear of that.
const MAX_COOKIE_BYTES = 4000;

// Anonymous preferences that used to live in localStorage and must follow the
// user across org subdomains, so they are stored as cookies with a lifetime.
const PERSISTENT_PREFERENCE_KEYS = new Set<StorageEnum>([
  StorageEnum.communitySelectorPreferences,
  StorageEnum.sortSettings,
  StorageEnum.paginationSetting,
  StorageEnum.paginationFilterStored,
]);

@Injectable({
  providedIn: 'root',
})
export class UserConfigManagementService extends BaseHttpService<any> {
  globalConfig: UserConfig = {};

  authenticated$: BehaviorSubject<boolean>;

  apiReady$: Observable<boolean>;

  private readonly API_PATH = 'accounts/my-preferences';

  private readonly userConfig$: Observable<UserConfigRes>;

  constructor(
    protected readonly http: HttpClient,
    private readonly authService: AuthenService,
    protected readonly sessionService: SessionService,
    private readonly cookie: CookieService
  ) {
    super(http);

    this.authenticated$ = this.authService.authenticated$;
    this.userConfig$ = this.http
      .get<UserConfigRes>(this.API_PATH)
      .pipe(shareReplay());

    this.apiReady$ = this.sessionService.apiReady$.pipe(filter(Boolean));
  }

  get(key: StorageEnum): Promise<any> {
    return this.asyncCaller(
      this.getUserConfig.bind(this, key),
      this.getLocalConfig.bind(this, key)
    );
  }

  set(key: StorageEnum, value: object, option?: object): Promise<unknown> {
    return this.asyncCaller(
      this.setUserConfig.bind(this, key, value),
      this.setLocalConfig.bind(this, key, value, option)
    );
  }

  private readonly asyncCaller = (
    localHandler,
    userHandler
  ): Promise<unknown> =>
    lastValueFrom(
      this.apiReady$.pipe(
        withLatestFrom(this.authenticated$),
        mergeMap(([_, authenticated]) =>
          authenticated ? localHandler() : userHandler()
        ),
        first()
      )
    );

  private setLocalConfig(
    key: StorageEnum,
    payload,
    option?: CookieOptions
  ): Observable<any> {
    if (this.isCrossSubdomainSharedKey(key)) {
      this.setSharedCookie(key, payload, option);
    } else {
      localStorage.setItem(key, JSON.stringify(payload));
    }

    return of(true);
  }

  private setSharedCookie(
    key: StorageEnum,
    payload: object,
    option?: CookieOptions
  ): void {
    const value = JSON.stringify(payload);
    const path = option?.path ?? '/';
    const domain = CookieUtils.getSharedDomain();

    // The library percent-encodes the value and that encoded form is what
    // counts toward the browser limit; past it the cookie is dropped silently.
    if (key.length + encodeURIComponent(value).length > MAX_COOKIE_BYTES) {
      console.warn(`${key} is too large for a cookie and was not saved.`);
      // Remove the previous value so a stale one is not served instead.
      this.cookie.delete(key, path, domain);

      return;
    }

    this.cookie.set(
      key,
      value,
      option?.expires ?? this.getDefaultCookieExpiry(key),
      path,
      domain,
      CookieUtils.isSecureConnection(),
      CookieUtils.sameSite
    );
  }

  private getDefaultCookieExpiry(key: StorageEnum): Date | undefined {
    if (!PERSISTENT_PREFERENCE_KEYS.has(key)) {
      return undefined;
    }

    return TimeUtils.getDateWithOffset(new Date(), 1) ?? undefined;
  }

  private setUserConfig(key: StorageEnum, value: object): Observable<unknown> {
    if (compare({ ...this.globalConfig[key] }, value)) {
      return of(true);
    }

    this.globalConfig[key] = value;

    return this.http.patch(this.API_PATH, { data: this.globalConfig });
  }

  private getLocalConfig(key: StorageEnum): Observable<unknown> {
    let result = this.isCrossSubdomainSharedKey(key)
      ? this.cookie.get(key)
      : localStorage.getItem(key);

    result = result ? JSON.parse(result) : undefined;

    return of(result);
  }

  private isCrossSubdomainSharedKey(key: StorageEnum): boolean {
    return (
      key === StorageEnum.globalFilters ||
      key === StorageEnum.orgFilterToken ||
      PERSISTENT_PREFERENCE_KEYS.has(key)
    );
  }

  private getUserConfig(key: StorageEnum): Observable<object> {
    const config = this.globalConfig[key];

    if (config) {
      return of(config);
    }

    return this.userConfig$.pipe(
      map(res => res || { data: {} }),
      tap(({ data }) => (this.globalConfig = data)),
      map(({ data }) => data[key])
    );
  }
}
